import type { ValidationError } from "class-validator";
import type { Dispatch, SetStateAction } from "react";
import type { useRouter } from "next/router";
import Auth, { DeliveryChannel } from "@Front/Api/Auth/Customer/Auth";
import { LoginStep } from "../useLoginController";
import {
	buildCustomerLoginAuthScopePayload,
	type CustomerAuthDisambiguation,
} from "@Front/Components/Layouts/Login/helpers/loginFolderUidHelpers";
import { postPublicEmailAuthCandidates } from "@Front/Api/Public/emailAuthCandidatesApi";
import type { CustomerLoginCandidateDto, EmailAuthCandidatesResponse } from "@Front/Api/Public/emailAuthCandidatesApi";
import { applyCustomerLoginFlowAfterMailVerifyNeedsChoice } from "@Front/Components/Layouts/Login/helpers/loginMailVerifyNeedsChoiceHelpers";
import { CUSTOMER_LOGIN_NO_ACTIVE_ACCESS_MESSAGE } from "@Front/Components/Layouts/Login/helpers/customerLoginNeedsChoiceMessages";
import type { CustomerLoginMulticandidateDecision } from "@Front/Components/Layouts/Login/helpers/loginCustomerMultiCandidateRouting";
import { decideCustomerLoginMultipleCandidatesFromEmailStep } from "@Front/Components/Layouts/Login/helpers/loginCustomerMultiCandidateRouting";

export interface UseEmailSubmissionOptions {
	router: ReturnType<typeof useRouter>;
	loginFolderUid: string | undefined;
	loginOfficeUid: string | undefined;
	loginCustomerUid: string | undefined;
	/** Lien / query avec folder, office ou customer : pas une connexion « e-mail seul ». */
	invitationScopeFromRouter: boolean;
	setEmail: (email: string) => void;
	setPartialPhoneNumber: (value: string) => void;
	setDeliveryChannel: (channel: DeliveryChannel) => void;
	setMaskedEmail: (email: string) => void;
	setTotpCodeUid: (uid: string) => void;
	setStep: (step: LoginStep) => void;
	setValidationErrors: (errors: ValidationError[]) => void;
	setLoginCandidates: (value: EmailAuthCandidatesResponse | null) => void;
	setAuthDisambiguation: Dispatch<SetStateAction<CustomerAuthDisambiguation>>;
	setCustomerTargetChoiceCandidates: Dispatch<SetStateAction<CustomerLoginCandidateDto[] | null>>;
}

/** Setters needed to run `mailVerifySms` after actor / target disambiguation (no router). */
export type MailVerifySmsScopeOpts = Pick<
	UseEmailSubmissionOptions,
	| "loginFolderUid"
	| "loginOfficeUid"
	| "loginCustomerUid"
	| "setPartialPhoneNumber"
	| "setDeliveryChannel"
	| "setMaskedEmail"
	| "setTotpCodeUid"
	| "setStep"
	| "setValidationErrors"
	| "setLoginCandidates"
>;

type MailVerifySmsStepParams = {
	email: string;
	loginFolderUid: string | undefined;
	loginOfficeUid: string | undefined;
	loginCustomerUid: string | undefined;
	authDisambiguation: CustomerAuthDisambiguation;
	setPartialPhoneNumber: (value: string) => void;
	setDeliveryChannel: (channel: DeliveryChannel) => void;
	setMaskedEmail: (email: string) => void;
	setTotpCodeUid: (uid: string) => void;
	setStep: (step: LoginStep) => void;
	setValidationErrors: (errors: ValidationError[]) => void;
	setLoginCandidates: (value: EmailAuthCandidatesResponse | null) => void;
};

function applyMailVerifySmsTotpDelivery(
	res: { partialPhoneNumber?: string | null; deliveryChannel: DeliveryChannel; maskedEmail?: string | null; totpCodeUid: string },
	p: Pick<MailVerifySmsStepParams, "setPartialPhoneNumber" | "setDeliveryChannel" | "setMaskedEmail" | "setTotpCodeUid" | "setStep" | "setValidationErrors">,
): void {
	p.setPartialPhoneNumber(res.partialPhoneNumber ?? "");
	p.setDeliveryChannel(res.deliveryChannel);
	p.setMaskedEmail(res.maskedEmail ?? "");
	p.setTotpCodeUid(res.totpCodeUid);
	p.setStep(LoginStep.TOTP);
	p.setValidationErrors([]);
}

export async function runCustomerMailVerifySmsStep(params: MailVerifySmsStepParams): Promise<void> {
	const res = await Auth.getInstance().mailVerifySms({
		email: params.email.trim(),
		...buildCustomerLoginAuthScopePayload(
			params.loginFolderUid,
			params.loginOfficeUid,
			params.authDisambiguation,
			params.loginCustomerUid,
		),
	});
	if (res.needsChoice) {
		applyCustomerLoginFlowAfterMailVerifyNeedsChoice(res, {
			setLoginCandidates: params.setLoginCandidates,
			setStep: params.setStep,
			setValidationErrors: params.setValidationErrors,
		});
		return;
	}
	const totpCodeUid = res.totpCodeUid;
	const deliveryChannel = res.deliveryChannel;
	if (!totpCodeUid || !deliveryChannel) {
		throw new Error("Réponse SMS inattendue");
	}
	applyMailVerifySmsTotpDelivery({ ...res, totpCodeUid, deliveryChannel }, params);
}

export function buildMailVerifySmsStepParams(
	emailValue: string,
	opts: MailVerifySmsScopeOpts,
	authDisambiguation: CustomerAuthDisambiguation,
): Parameters<typeof runCustomerMailVerifySmsStep>[0] {
	return {
		email: emailValue,
		loginFolderUid: opts.loginFolderUid,
		loginOfficeUid: opts.loginOfficeUid,
		loginCustomerUid: opts.loginCustomerUid,
		authDisambiguation,
		setPartialPhoneNumber: opts.setPartialPhoneNumber,
		setDeliveryChannel: opts.setDeliveryChannel,
		setMaskedEmail: opts.setMaskedEmail,
		setTotpCodeUid: opts.setTotpCodeUid,
		setStep: opts.setStep,
		setValidationErrors: opts.setValidationErrors,
		setLoginCandidates: opts.setLoginCandidates,
	};
}

export type ApplyCustomerLoginMulticandidateOpts = MailVerifySmsScopeOpts &
	Pick<
		UseEmailSubmissionOptions,
		"setStep" | "setValidationErrors" | "setAuthDisambiguation" | "setCustomerTargetChoiceCandidates"
	>;

export async function applyCustomerLoginMulticandidateDecision(
	decision: CustomerLoginMulticandidateDecision,
	emailValue: string,
	opts: ApplyCustomerLoginMulticandidateOpts,
): Promise<void> {
	switch (decision.kind) {
		case "office_choice":
			opts.setStep(LoginStep.CUSTOMER_OFFICE_CHOICE);
			opts.setValidationErrors([]);
			return;
		case "target_choice":
			opts.setCustomerTargetChoiceCandidates(decision.narrowedCandidates ?? null);
			opts.setStep(LoginStep.CUSTOMER_TARGET_CHOICE);
			opts.setValidationErrors([]);
			return;
		case "mail_verify":
			opts.setAuthDisambiguation(decision.disambiguation);
			await runCustomerMailVerifySmsStep(buildMailVerifySmsStepParams(emailValue, opts, decision.disambiguation));
			return;
	}
}

async function routeLoginNoCustomerCandidates(
	emailValue: string,
	candidates: EmailAuthCandidatesResponse,
	opts: UseEmailSubmissionOptions,
): Promise<void> {
	if (candidates.thirdPartyCandidates.length > 0) {
		opts.router.push(`/third-party/login?email=${encodeURIComponent(emailValue)}`);
		return;
	}
	opts.setValidationErrors([
		{
			property: "email",
			constraints: {
				no_active_access: CUSTOMER_LOGIN_NO_ACTIVE_ACCESS_MESSAGE,
			},
		} as ValidationError,
	]);
}

async function routeLoginSingleCustomerCandidate(
	emailValue: string,
	candidates: EmailAuthCandidatesResponse,
	opts: UseEmailSubmissionOptions,
): Promise<void> {
	const row = candidates.customerCandidates[0];
	if (!row) {
		return;
	}
	const nextDisambiguation: CustomerAuthDisambiguation = {
		customerUid: row.customerUid,
		folderUid: row.folderUid,
		officeUid: row.officeUid,
	};
	opts.setAuthDisambiguation(nextDisambiguation);
	await runCustomerMailVerifySmsStep(buildMailVerifySmsStepParams(emailValue, opts, nextDisambiguation));
}

async function routeLoginAfterEmailCandidatesLoaded(
	emailValue: string,
	candidates: EmailAuthCandidatesResponse,
	opts: UseEmailSubmissionOptions,
): Promise<void> {
	if (candidates.actorChoiceRequired) {
		opts.setStep(LoginStep.ACTOR_CHOICE);
		opts.setValidationErrors([]);
		return;
	}

	if (candidates.customerCandidates.length === 0) {
		await routeLoginNoCustomerCandidates(emailValue, candidates, opts);
		return;
	}

	if (candidates.customerCandidates.length > 1) {
		const decision = decideCustomerLoginMultipleCandidatesFromEmailStep(
			candidates.customerCandidates,
			opts.invitationScopeFromRouter,
		);
		await applyCustomerLoginMulticandidateDecision(decision, emailValue, opts);
		return;
	}

	await routeLoginSingleCustomerCandidate(emailValue, candidates, opts);
}

export async function runLoginEmailFormSubmit(
	values: { [key: string]: string },
	opts: UseEmailSubmissionOptions,
): Promise<void> {
	if (!values["email"]) return;
	const emailValue = values["email"].trim();
	opts.setEmail(emailValue);
	opts.setAuthDisambiguation({});
	opts.setCustomerTargetChoiceCandidates(null);
	opts.setLoginCandidates(null);

	const candidates = await postPublicEmailAuthCandidates({
		email: emailValue,
		...buildCustomerLoginAuthScopePayload(opts.loginFolderUid, opts.loginOfficeUid, {}, opts.loginCustomerUid),
	});
	opts.setLoginCandidates(candidates);

	await routeLoginAfterEmailCandidatesLoaded(emailValue, candidates, opts);
}
